/**
 * BF offer — the sofa PDP side (v2, 7 Sep 2026).
 *
 * The offer: sofa + Stoffbezug -> Kissen-Set 1 AND Komplettes Schutzset free.
 *
 * Since v3 (8 Sep 2026) the sofa PDP puts it as a choice between two cards —
 * the sofa on its own, or the 2-in-1 set — with the set the one that comes
 * chosen. Underneath, nothing about the sale changed: the cover's checkbox
 * inside the set card is still the only state there is, and everything that
 * already watched it (upsell.js and its slot inputs, the sticky bar,
 * kc-bf-cart-gift.js) keeps working on exactly the machinery it knows. This
 * file:
 *
 *   1. drives that checkbox from whichever card the customer picks, and
 *      paints which one is chosen (renderChoice, selectChoice)
 *   2. keeps every number honest — both cards' prices, the set's three
 *      lines, both totals and the nudge — from the prices Shopify already
 *      charges (updateTotals). It prices nothing: the two gifts are struck
 *      through at their own price and taken to zero by the automatic
 *      discounts in the admin
 *   3. keeps the lines showing the variant that will actually be added, with
 *      its own picture: the cover and set rows follow the picks made in the
 *      variant modals (upsell:variant-changed), and the gift set re-matches
 *      the sofa's fabric until picked by hand
 *   4. pushes the two gift lines into the SAME add-to-cart call as the sofa
 *      and the cover (upsell:collect-items, dispatched by upsell.js), tagged
 *      `_bf_gift` so the cart can tell them from a set the customer bought
 *
 * It never writes to the cart on its own. v1 had a PDP reconciler that added
 * and removed gift lines behind the customer's back and a second writer in
 * the cart doing the same; they fought. Corrections after the add — the
 * cover removed, quantities, carts assembled elsewhere — belong to
 * kc-bf-cart-gift.js, the single writer, on every page including this one.
 *
 * Plain IIFE, delegated listeners, state kept in the DOM: the upsell block is
 * `data-preserve-on-rerender`, i.e. CLONED across the sofa's variant
 * re-renders, and a clone keeps attributes, values and checkedness but no JS
 * closures. Everything this file needs later is an attribute on the
 * `[data-bf-config]` node or on the summary rows.
 */
(function () {
  "use strict";

  // A PDP can carry more than one upsell block, each emitting its own
  // <script src>; the second copy must be a no-op.
  if (window.__kcBfOfferLoaded) return;
  window.__kcBfOfferLoaded = true;

  var GIFT_PROPERTY = "_bf_gift";

  // ── helpers ───────────────────────────────────────────────────────────────

  function num(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  function parseJson(raw, fallback) {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return fallback;
    }
  }

  /** Close enough to Liquid's `handle` for fabric names ("Rosé-Cord" -> "rose-cord"). */
  function handleize(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * Shopify money formatting from `shop.money_format`, cents in. Covers the
   * placeholders Shopify offers; this store's is
   * `{{amount_with_comma_separator}} €` (1.234,56 €).
   */
  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = placeholder.exec(format || "");
    var key = match ? match[1] : "amount";

    function render(precision, thousands, decimal) {
      var parts = value.toFixed(precision).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }

    var amount;
    switch (key) {
      case "amount_no_decimals":
        amount = render(0, ",", ".");
        break;
      case "amount_with_comma_separator":
        amount = render(2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        amount = render(0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        amount = render(2, "'", ".");
        break;
      case "amount_with_space_separator":
        amount = render(2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        amount = render(0, " ", ",");
        break;
      case "amount_with_period_and_space_separator":
        amount = render(2, " ", ".");
        break;
      default:
        amount = render(2, ",", ".");
    }
    return match ? format.replace(placeholder, amount) : amount;
  }

  // ── per-block lookups (state lives in the DOM) ────────────────────────────

  function configNodes() {
    return document.querySelectorAll("[data-bf-config]");
  }

  function rootOf(node) {
    return node ? node.closest("kc-upsell") : null;
  }

  function summaryOf(root) {
    return root ? root.querySelector("[data-bf-summary]") : null;
  }

  // Anywhere in the root, not only in the panel: the Black Weeks layout moves
  // the set's lines into the set card (placeSetPanel).
  function rowOf(root, kind) {
    return root ? root.querySelector('[data-bf-row="' + kind + '"]') : null;
  }

  /**
   * Black Weeks layout only (block modifier `upsell-block--bf-dark`): the
   * set's three lines are drawn INSIDE the set card, so the panel that holds
   * them is moved there once. Idempotent, and the block is cloned rather than
   * re-rendered on variant change, so the move survives. The panel keeps its
   * `is-active` class; CSS hides it while the card is not the chosen one.
   */
  function placeSetPanel(root) {
    // the block wraps <kc-upsell>, so the modifier is an ancestor of the root
    if (!root || !root.closest(".upsell-block--bf-dark")) return;
    var panel = root.querySelector('[data-bf-panel="set"]');
    var card = root.querySelector('[data-bf-choice="set"] .upsell-info');
    if (panel && card && !card.contains(panel)) card.appendChild(panel);
  }

  /** A solo sofa (Nora): its card item is the model's gift set, there is no cover. See _kc-product-detail-upsell. */
  function isSolo(config) {
    return !!(config && config.getAttribute("data-bf-solo"));
  }

  /**
   * Solo: the gift set follows the sofa's colour until the customer picks one
   * by hand — in the card's own slot input and checkbox (what upsell.js adds),
   * the config and the row.
   */
  function applySoloGift(config, variant) {
    var root = rootOf(config);
    if (!root) return;
    config.setAttribute("data-bf-cover-variant-id", variant.id);
    if (variant.p != null) config.setAttribute("data-bf-cover-price", variant.p);
    if (variant.c != null) config.setAttribute("data-bf-cover-compare", variant.c);
    var key = config.getAttribute("data-bf-cover-key");
    var slot = root.querySelector('input[data-upsell-item][data-input-id="' + key + '"]');
    if (slot) slot.value = variant.id;
    var checkbox = coverCheckbox(root);
    if (checkbox) checkbox.value = variant.id;
    var card = root.querySelector(".upsell-block-item--bf-cover");
    if (card) card.setAttribute("data-current-variant-id", variant.id);
    var row = rowOf(root, "cover");
    setRowText(row, "[data-bf-row-variant]", variant.t || "");
    if (variant.img) setRowImage(row, variant.img, variant.t);
  }

  function coverCheckbox(root) {
    var card = root ? root.querySelector(".upsell-block-item--bf-cover") : null;
    return card ? card.querySelector('input[name="upsell"]') : null;
  }

  /**
   * Ticked AND visible: upsell.js ignores a checked box inside a CSS-hidden
   * card (the product-limit rules), and the gifts must not outrun it.
   */
  function coverChecked(root) {
    var checkbox = coverCheckbox(root);
    if (!checkbox || !checkbox.checked) return false;
    var card = checkbox.closest(".upsell-block-item");
    return !card || getComputedStyle(card).display !== "none";
  }

  function setRowText(row, selector, text) {
    if (!row) return;
    var el = row.querySelector(selector);
    if (el) el.textContent = text == null ? "" : String(text);
  }

  function setRowImage(row, src, alt) {
    if (!row || !src) return;
    var thumb = row.querySelector("[data-bf-row-thumb]");
    if (!thumb) return;
    var img = thumb.querySelector("img");
    if (!img) {
      img = document.createElement("img");
      img.loading = "lazy";
      thumb.appendChild(img);
    }
    img.src = src;
    // srcset would otherwise keep winning over the new src.
    img.removeAttribute("srcset");
    if (alt) img.alt = alt;
  }

  // ── the decision ──────────────────────────────────────────────────────────
  //
  // Two cards, one answer. The cover's checkbox is still the only state there
  // is — the cards read it and write to it, nothing more. That is deliberate:
  // upsell.js builds the cart from that checkbox and its slot inputs, the
  // sticky bar mirrors it, kc-bf-cart-gift.js reconciles from what it put in
  // the cart. A second source of truth would be a second thing to disagree.

  function configOf(root) {
    return root ? root.querySelector("[data-bf-config]") : null;
  }

  function choiceOf(root) {
    return coverChecked(root) ? "set" : "solo";
  }

  /** Ticks or unticks the cover, and lets every existing listener follow. */
  function selectChoice(root, choice) {
    var checkbox = coverCheckbox(root);
    if (!checkbox) return;

    var wanted = choice === "set";
    if (checkbox.checked === wanted) {
      renderChoice(root);
      return;
    }

    checkbox.checked = wanted;
    // Bubbles, so upsell.js enables or disables the cover's slot inputs and
    // the change listener below repaints — exactly as a click on the
    // checkbox itself would.
    checkbox.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /** Which card looks chosen, what its button says, which panel is open. */
  function renderChoice(root) {
    if (!root) return;
    placeSetPanel(root);

    var choice = choiceOf(root);
    var config = configOf(root);
    var take = (config && config.getAttribute("data-bf-label-take")) || "";
    var taken = (config && config.getAttribute("data-bf-label-taken")) || "";

    each(root.querySelectorAll("[data-bf-choice]"), function (card) {
      var chosen = card.getAttribute("data-bf-choice") === choice;
      card.classList.toggle("is-chosen", chosen);
      // The button is what assistive tech reads: its state and its copy both
      // say which card is live, so the choice does not depend on the border.
      var cta = card.querySelector("[data-bf-cta]");
      if (!cta) return;
      cta.setAttribute("aria-pressed", chosen ? "true" : "false");
      if (take && taken) cta.textContent = chosen ? taken : take;
    });

    var summary = summaryOf(root);
    if (!summary) return;
    swapPanels(summary, choice);
  }

  /**
   * Shows the panel for `choice` and animates the wrapper between the two
   * heights.
   *
   * The panels have different heights — the set lists three lines, the sofa
   * on its own has a total and a nudge — and each takes its own space rather
   * than both holding open a cell sized for the taller. Everything ABOVE the
   * panel (the two cards, which is where the customer just tapped) stays
   * exactly where it is either way; what moves is what comes after it, and
   * that cannot be avoided while the panel is honest about its size. So the
   * change is animated: 220ms from the old height to the new one reads as
   * the panel resizing, where an instant swap reads as the page jumping.
   *
   * The inline height is always cleared at the end, and skipped entirely on
   * first paint and under prefers-reduced-motion, so the wrapper is never
   * left pinned to a stale number.
   */
  function swapPanels(summary, choice) {
    var wrap = summary.querySelector(".kc-bf-summary__panels");
    var panels = summary.querySelectorAll("[data-bf-panel]");

    function apply() {
      each(panels, function (panel) {
        panel.classList.toggle("is-active", panel.getAttribute("data-bf-panel") === choice);
      });
    }

    var reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Nothing to animate from before the panel has been laid out once.
    var from = wrap ? wrap.getBoundingClientRect().height : 0;
    if (!wrap || !from || reduced) {
      apply();
      return;
    }

    apply();
    wrap.style.height = "auto";
    var to = wrap.getBoundingClientRect().height;
    if (to === from) {
      wrap.style.height = "";
      return;
    }

    wrap.style.height = from + "px";
    // Read back, so the browser has a start value to animate away from.
    void wrap.offsetHeight;
    wrap.style.transition = "height 220ms ease";
    wrap.style.height = to + "px";

    clearTimeout(wrap._kcBfResize);
    wrap._kcBfResize = setTimeout(function () {
      wrap.style.transition = "";
      wrap.style.height = "";
    }, 240);
  }

  // ── the panel ─────────────────────────────────────────────────────────────

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function setText(scope, selector, text) {
    if (!scope) return;
    each(scope.querySelectorAll(selector), function (el) {
      el.textContent = text == null ? "" : String(text);
    });
  }

  /** "-45%", or "" when there is nothing struck through to compare against. */
  function offLabel(price, compare) {
    if (!compare || compare <= price) return "";
    return "-" + Math.round(((compare - price) * 100) / compare) + "%";
  }

  /**
   * The cover's price as the checkout will charge it. Black Weeks 2026 takes
   * the model's percentage off every cover (kc-bf-bundle bf[20]; the Function
   * decides, `data-bf-cover-off` only repeats it here), so the list price
   * becomes the struck-through figure. The voucher keeps its own price: its
   * amount IS the voucher (kc-cover-voucher.js marks it on the config).
   */
  /**
   * A solo set's gifts at their LIST price (compare-at; today's price when there is none): what the offer strikes
   * through and counts in the saving (user 26.10: the gifts' own prices dropped for Black Weeks).
   */
  function listPrice(price, compare) {
    return compare > price ? compare : price;
  }
  function soloGiftsList(config) {
    return listPrice(num(config.getAttribute("data-bf-cover-price")), num(config.getAttribute("data-bf-cover-compare")))
      + listPrice(num(config.getAttribute("data-bf-schutzset-price")), num(config.getAttribute("data-bf-schutzset-compare")));
  }

  function coverPricesOf(config) {
    var price = num(config.getAttribute("data-bf-cover-price"));
    var compare = num(config.getAttribute("data-bf-cover-compare"));
    var off = num(config.getAttribute("data-bf-cover-off"));
    // Solo: the card's item is a gift. Free, and its worth joins the Schutzset's in the struck figure.
    if (isSolo(config)) {
      // the set's price on top of the sofa (bf[25]); the gifts themselves stay free
      return { price: num(config.getAttribute("data-bf-solo-extra")), compare: soloGiftsList(config) };
    }
    if (off > 0 && !config.hasAttribute("data-kc-voucher-variant-id")) {
      if (compare < price) compare = price;
      price = Math.round((price * (100 - off)) / 100);
    }
    return { price: price, compare: compare };
  }

  /** Fills [amount] / [setoff] / [solooff] / [gifts] in a copy template. */
  function fillTemplate(el, values) {
    if (!el) return;
    var tpl = el.getAttribute("data-bf-tpl");
    if (!tpl) return;
    var out = tpl;
    for (var key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        out = out.split("[" + key + "]").join(values[key]);
      }
    }
    el.textContent = out;
  }

  /**
   * The template block that holds the phone's buy area: badges, the product's
   * price, the USPs and the buttons. It is `hide_on_desktop`, so on a wide
   * screen it is `display: none` and `paintProductPrice` walks away — which
   * is exactly the intent: on desktop that price sits ABOVE the choice and
   * must not move.
   *
   * Matched by the runtime id `kc-content-group.liquid` stamps on its own
   * root (`data-block-id-cg`), suffix-matched so the per-template prefix
   * doesn't matter. Same block key in all nine sofa templates.
   */
  var MOBILE_BUY_GROUP = "kc_content_group_FDVcCx";
  // Nora's v2 PDP also shows the price above the colours (desktop) / the USPs (phone): the same painting.
  var PRICE_GROUPS = [MOBILE_BUY_GROUP, "kc_content_group_bf_price"];

  /**
   * The theme's price-list drops the cents when they are zero; match it.
   *
   * Anchored to the DECIMAL separator — the one not followed by another
   * digit. A plain `.replace(",00","").replace(".00","")` (which is what
   * price-list.liquid does with two `remove` filters) also eats the thousands
   * separator of a round price: "5.000,00 €" loses ",00", then ".00" matches
   * inside "5.000" and the customer is quoted "50 €". Real sofa prices
   * (3.840, 7.250) never hit it, which is why it has gone unnoticed there —
   * but a 1.000/5.000/9.000 total would.
   */
  function trimZeroCents(text) {
    return String(text).replace(/([.,])00(?!\d)/, "");
  }

  /**
   * The product's OWN price block, on the phone, follows the chosen card.
   *
   * On a phone the choice comes BEFORE this block, so a customer who picks
   * the 2-in-1 set then reads a price that is the sofa's alone — the number
   * they are about to be charged is 985 € higher and nothing on screen says
   * so. All four parts move together (price, struck-through, -% badge and
   * "Spare X"), because a block where only the big number follows the choice
   * contradicts itself.
   *
   * The desktop copy of this price is a DIFFERENT template block, above the
   * cards, and is deliberately left alone.
   */
  function paintProductPrice(config) {
    var root = rootOf(config);
    if (!root) return;
    var scope = root.closest("product-rerender") || root.closest(".shopify-section");
    if (!scope) return;

    PRICE_GROUPS.forEach(function (id) {
      var group = scope.querySelector('[data-block-id-cg$="' + id + '"]');
      // painted hidden too: a window widened or narrowed later shows it, and an unpainted box quoted the sofa alone
      if (group) paintPriceGroup(config, root, group);
    });
  }

  function paintPriceGroup(config, root, group) {
    var format = config.getAttribute("data-bf-money-format") || "{{amount}}";
    var sofa = num(config.getAttribute("data-bf-sofa-price"));
    var sofaCompare = num(config.getAttribute("data-bf-sofa-compare"));
    var coverPrices = coverPricesOf(config);
    var cover = coverPrices.price;
    var coverCompare = coverPrices.compare;
    if (sofaCompare < sofa) sofaCompare = sofa;
    if (coverCompare < cover) coverCompare = cover;

    var withSet = choiceOf(root) === "set";
    var price = withSet ? sofa + cover : sofa;
    // a solo set (Nora): the price block strikes the sofa's own list price; the set's sits on the Total line only
    var compare = withSet && !isSolo(config) ? sofaCompare + coverCompare : sofaCompare;
    var onSale = compare > price;

    var list = group.querySelector(".price-list");
    if (list) {
      // The theme's own markup, rebuilt: the visually-hidden label is kept
      // rather than re-invented, so screen readers still hear "sale price".
      var sr = list.querySelector(".sr-only");
      var html =
        '<sale-price class="' +
        (onSale ? "text-on-sale" : "text-subdued") +
        ' price-regular-weight">' +
        (sr ? sr.outerHTML : "") +
        trimZeroCents(formatMoney(price, format)) +
        "</sale-price>";
      if (onSale) {
        html +=
          '<compare-at-price class="text-subdued line-through price-regular-weight">' +
          trimZeroCents(formatMoney(compare, format)) +
          "</compare-at-price>";
      }
      list.innerHTML = html;
    }

    // Two badges: "-47%" and "Spare 3.410,00 €". Which is which is decided
    // once, from the copy Liquid rendered, and remembered — after the first
    // repaint the "%" is no longer a reliable tell.
    var announced = num(config.getAttribute("data-bf-display-discount"));
    var template = config.getAttribute("data-bf-badge-save") || "";
    each(group.querySelectorAll("on-sale-badge"), function (badge) {
      var role = badge.getAttribute("data-bf-badge");
      if (!role) {
        role = badge.textContent.indexOf("%") !== -1 ? "percent" : "amount";
        badge.setAttribute("data-bf-badge", role);
      }
      badge.hidden = !onSale;
      if (!onSale) return;
      if (role === "percent") {
        // The set repeats the campaign's announced figure, so the badge and
        // the card cannot disagree; the sofa alone keeps its real discount.
        badge.textContent =
          withSet && announced ? "-" + announced + "%" : offLabel(price, compare);
      } else {
        var amount = formatMoney(compare - price, format);
        // A badge block set to "Hide zero cents" (kc-badge-sale, trim_cents)
        // keeps that when repainted.
        if (badge.closest("[data-kc-trim-cents]")) amount = trimZeroCents(amount);
        badge.textContent = template ? template.split("[amount]").join(amount) : amount;
      }
    });
  }

  /**
   * Every number on screen, from the config's live values: the two cards'
   * prices, the three lines of the set, both totals and the nudge.
   *
   * Nothing here prices anything — it adds up what Shopify already charges.
   * The set's total is the sofa plus the cover; the two gifts are struck
   * through at their own price and taken to zero by the automatic discounts
   * in the admin, so they are outside both the total and the saving. A
   * saving that counted them would not match the struck-through figure next
   * to it, and a number the customer cannot reconcile is worse than a
   * smaller one they can.
   */
  function updateTotals(config) {
    var root = rootOf(config);
    if (!root) return;

    var format = config.getAttribute("data-bf-money-format") || "{{amount}}";
    var sofa = num(config.getAttribute("data-bf-sofa-price"));
    var sofaCompare = num(config.getAttribute("data-bf-sofa-compare"));
    var coverPrices = coverPricesOf(config);
    var cover = coverPrices.price;
    var coverCompare = coverPrices.compare;
    var setPrice = num(config.getAttribute("data-bf-set-price"));
    var schutzsetPrice = num(config.getAttribute("data-bf-schutzset-price"));

    // A missing or lower compare-at price means "no discount on this line",
    // never a negative saving.
    if (sofaCompare < sofa) sofaCompare = sofa;
    if (coverCompare < cover) coverCompare = cover;

    // The announced discount (bf[18], "spare bis zu 52%"). The campaign is
    // communicated with ONE figure and the card has to repeat it; the
    // computed percentage moves with each sofa's own prices and contradicted
    // the headline. Empty or 0 falls back to the real calculation.
    var announced = num(config.getAttribute("data-bf-display-discount"));

    var dealPrice = sofa + cover;
    var dealCompare = sofaCompare + coverCompare;
    var dealSaving = dealCompare - dealPrice;
    var soloSaving = sofaCompare - sofa;

    // ── the two cards ──
    setText(root, "[data-bf-solo-price]", formatMoney(sofa, format));
    setText(root, "[data-bf-solo-compare]", sofaCompare > sofa ? formatMoney(sofaCompare, format) : "");
    setText(root, "[data-bf-solo-off]", offLabel(sofa, sofaCompare));

    setText(root, "[data-bf-deal-price]", formatMoney(dealPrice, format));
    setText(root, "[data-bf-deal-compare]", dealCompare > dealPrice ? formatMoney(dealCompare, format) : "");
    setText(
      root,
      "[data-bf-deal-off]",
      announced ? "-" + announced + "%" : offLabel(dealPrice, dealCompare)
    );
    // A solo model's set (Nora): the sofa line quotes the sofa's own list price and saving; the struck total
    // of all three items and the percentage sit on the Total line under the gifts.
    // ...and quotes the SET on its sofa line too (Ivan 23/10: every price of the offer agrees): all three items
    // at their list price struck, and that saving under it; the percentage sits on the Total line only.
    var solo = isSolo(config);
    var soloCompare = sofaCompare + soloGiftsList(config);
    if (solo) {
      // the sofa line: the sofa's own list price (user 26.10); the set's sits on the Total line
      setText(root, "[data-bf-deal-compare]", sofaCompare > dealPrice ? formatMoney(sofaCompare, format) : "");
      setText(root, "[data-bf-deal-off]", "");
    }

    // The product's own price block on the phone follows the same choice.
    // BEFORE the summary guard below: if the panel were ever missing, the
    // customer would still be reading a price that contradicts their choice,
    // which is worse than a missing panel.
    paintProductPrice(config);

    // The last-chance pop-up says what skipping the cover gives up: the cover's own
    // discount plus the two gifts at their own prices. Outside `root` (the pop-up
    // is moved to <body>), so it is looked up on the document.
    var coverSaving = coverCompare - cover;
    var giftsValue = setPrice + schutzsetPrice;
    // [saving] cover discount, [price]/[full] the cover in the set / on its own, [gifts] both gifts,
    // [amount] saving + gifts. The locale strings pick what they quote.
    var popupValues = {
      saving: formatMoney(coverSaving, format),
      price: formatMoney(cover, format),
      full: formatMoney(coverCompare, format),
      gifts: formatMoney(giftsValue, format),
      amount: formatMoney(coverSaving + giftsValue, format),
    };
    var lossEls = document.querySelectorAll("[data-bf-popup-loss]");
    var lossDetailEls = document.querySelectorAll("[data-bf-popup-loss-detail]");
    for (var li = 0; li < lossEls.length; li++) {
      if (coverSaving + giftsValue > 0) fillTemplate(lossEls[li], popupValues);
      else lossEls[li].textContent = "";
    }
    for (var ld = 0; ld < lossDetailEls.length; ld++) {
      fillTemplate(lossDetailEls[ld], popupValues);
    }

    var summary = summaryOf(root);
    if (!summary) return;

    // ── the set's three lines ──
    if (isSolo(config)) {
      // the gift set's row: its own price, struck through
      setRowText(rowOf(root, "cover"), "[data-bf-row-compare]", formatMoney(listPrice(num(config.getAttribute("data-bf-cover-price")), num(config.getAttribute("data-bf-cover-compare"))), format));
    } else {
      setRowText(rowOf(root, "cover"), "[data-bf-row-price]", formatMoney(cover, format));
      setRowText(
        rowOf(root, "cover"),
        "[data-bf-row-compare]",
        coverCompare > cover ? formatMoney(coverCompare, format) : ""
      );
    }
    // the gifts at their LIST price (compare-at), as on Nora: what each gift is worth (user 29.10)
    setRowText(rowOf(root, "set"), "[data-bf-row-price]", formatMoney(listPrice(setPrice, num(config.getAttribute("data-bf-set-compare"))), format));
    setRowText(rowOf(root, "schutzset"), "[data-bf-row-price]", formatMoney(listPrice(schutzsetPrice, num(config.getAttribute("data-bf-schutzset-compare"))), format));

    // ── both footers ──
    setText(summary, "[data-bf-total]", formatMoney(dealPrice, format));
    setText(summary, "[data-bf-solo-total]", formatMoney(sofa, format));

    fillTemplate(summary.querySelector("[data-bf-saving]"), {
      amount: formatMoney(dealSaving, format),
    });
    // the same figure under the set card's price (Black Weeks layout); a solo model's sofa line: the sofa's own saving
    var lineSaving = solo ? sofaCompare - dealPrice : dealSaving;
    var dealSavingEl = root.querySelector("[data-bf-deal-saving]");
    if (solo && lineSaving <= 0) {
      if (dealSavingEl) dealSavingEl.textContent = "";
    } else {
      fillTemplate(dealSavingEl, { amount: formatMoney(lineSaving, format) });
    }
    fillTemplate(summary.querySelector("[data-bf-solo-saving]"), {
      amount: formatMoney(soloSaving, format),
    });

    // A solo model's Total line: everything at list price struck, the set's price, and the percentage pill
    // (the announced figure when there is one, so it repeats the headline).
    if (solo) {
      var totalCompare = soloCompare;
      setText(root, "[data-bf-total-price]", formatMoney(dealPrice, format));
      setText(root, "[data-bf-total-compare]", totalCompare > dealPrice ? formatMoney(totalCompare, format) : "");
      // computed from the list prices (user 26.10), and repeated in the title's [pct]
      var pct = totalCompare > dealPrice ? Math.round(((totalCompare - dealPrice) * 100) / totalCompare) : 0;
      var titlePcts = root.querySelectorAll("[data-bf-title-pct]");
      for (var tp = 0; tp < titlePcts.length; tp++) if (pct) titlePcts[tp].textContent = pct;
      var pill = root.querySelector("[data-bf-total-off]");
      if (pill) {
        pill.hidden = !pct;
        if (pct) {
          fillTemplate(pill, { amount: pct + "%" });
          // the figure large, the word small and in its own span (phones show the figure only); the locale text
          // holds no markup
          var label = pill.textContent, figure = pct + "%", at = label.indexOf(figure);
          if (at !== -1) {
            pill.textContent = "";
            var before = document.createElement("span");
            before.className = "kc-bf-total__word";
            before.textContent = label.slice(0, at);
            var big = document.createElement("b");
            big.textContent = figure;
            var after = document.createElement("span");
            after.className = "kc-bf-total__word";
            after.textContent = label.slice(at + figure.length);
            pill.appendChild(before);
            pill.appendChild(big);
            pill.appendChild(after);
          }
        }
      }
    }

    // ── the nudge ──
    // What taking the cover adds to the saving: the cover's own discount.
    // The gifts are named alongside it rather than priced into it, for the
    // same reason the set's saving leaves them out.
    var nudgeMeta = summary.querySelector("[data-bf-nudge-meta]");
    fillTemplate(summary.querySelector("[data-bf-nudge-title]"), {
      amount: formatMoney(dealSaving - soloSaving, format),
    });
    fillTemplate(nudgeMeta, {
      setoff: announced ? "-" + announced + "%" : offLabel(dealPrice, dealCompare),
      solooff: offLabel(sofa, sofaCompare),
      gifts: (nudgeMeta && nudgeMeta.getAttribute("data-bf-gifts")) || "",
      // the cover on its own, for copy that quotes it ("+665 € statt 1.710 €")
      cover: formatMoney(cover, format),
      coverfull: formatMoney(coverCompare, format),
    });
  }

  /**
   * Applies a Kissen-Set variant to the row, its reference checkbox (what the
   * variant modal preselects from) and the config (what goes into the cart).
   */
  function applySetVariant(config, variant, picked) {
    var root = rootOf(config);
    var row = rowOf(root, "set");

    config.setAttribute("data-bf-set-variant-id", variant.id);
    if (variant.price != null) config.setAttribute("data-bf-set-price", variant.price);
    if (variant.compareAt !== undefined) config.setAttribute("data-bf-set-compare", variant.compareAt || 0);
    // A pick by hand sticks: the sofa's fabric no longer overrides it.
    if (picked) config.setAttribute("data-bf-set-picked", "true");

    if (!row) return;
    row.setAttribute("data-current-variant-id", variant.id);
    if (variant.price != null) row.setAttribute("data-current-variant-price", variant.price);
    var reference = row.querySelector("input[data-upsell-product-key]");
    if (reference) reference.value = variant.id;
    setRowText(row, "[data-bf-row-variant]", variant.title || "");
    if (variant.img) setRowImage(row, variant.img, variant.title);
  }

  /** The Kissen-Set variant in a given fabric (exact handle first), or null. */
  function matchSetVariant(config, option1) {
    var variants = parseJson(config.getAttribute("data-bf-set-variants"), []);
    var wanted = handleize(option1);
    if (!wanted || !variants.length) return null;

    var exact = variants.filter(function (v) {
      return v.o === wanted;
    })[0];
    if (exact) return exact;

    return (
      variants.filter(function (v) {
        return v.o && (v.o.indexOf(wanted) !== -1 || wanted.indexOf(v.o) !== -1);
      })[0] || null
    );
  }

  /**
   * The sofa's own row: price, compare-at, fabric name and picture, from the
   * map Liquid emitted. `variant:change` carries none of those, and this block
   * is preserved (cloned) rather than re-rendered, so nothing else would bring
   * them up to date.
   */
  function applySofaVariant(config, variant) {
    var variants = parseJson(config.getAttribute("data-bf-sofa-variants"), {});
    var data = variants[String(variant.id)] || null;

    config.setAttribute("data-bf-sofa-variant-id", variant.id);
    if (data) {
      config.setAttribute("data-bf-sofa-price", data.p);
      config.setAttribute("data-bf-sofa-compare", data.c || 0);
    }

    // The two cards' sofa photos ("Sofa + Wechselbezug", "Nur Sofa") show the chosen colour too.
    if (data && data.i) {
      rootOf(config).querySelectorAll("[data-bf-sofa-thumb] img").forEach(function (img) {
        img.src = data.i;
        img.removeAttribute("srcset");
      });
    }

    var row = rowOf(rootOf(config), "sofa");
    if (!row) return;

    var title = (data && data.t) || variant.title || "";
    if (title === "Default Title") title = "";
    setRowText(row, "[data-bf-row-variant]", title);
    if (data && data.i) setRowImage(row, data.i, title);
  }

  // ── events ────────────────────────────────────────────────────────────────

  // The cover's checkbox: the panel and the price follow it — also when
  // upsell.js syncs a sibling checkbox, which dispatches the same bubbling
  // change event.
  document.addEventListener("change", function (event) {
    var checkbox = event.target;
    if (!checkbox || checkbox.name !== "upsell") return;
    var card = checkbox.closest(".upsell-block-item--bf-cover");
    if (!card) return;

    var root = rootOf(card);
    renderChoice(root);
    configNodes().forEach(function (config) {
      if (rootOf(config) === root) updateTotals(config);
    });
    // Nothing is scrolled into view. Picking a card must leave the page
    // exactly where the customer left it: both cards and the panel are one
    // block, the panel swaps content rather than unfolding, and moving the
    // viewport under a finger that just tapped is disorienting.
  });

  // The two cards. The button is the control; the card around it is a bigger
  // target for the same thing.
  //
  // CAPTURE PHASE, and it stops the event there. upsell.js binds its own
  // click handler to every `[data-upsell-matcher]` — which the set card is —
  // and that one TOGGLES the checkbox. Toggling is right for a list of
  // independent upsells and wrong for a pair of radios: clicking the card
  // you already chose would clear it. Taking the event on the way down
  // leaves the element handler unreached and this file with the only say.
  //
  // Anything already interactive inside a card keeps its own job and is left
  // alone — links, and the hidden fabric picker the reminder clicks. That
  // click still reaches upsell.js's handler, which ignores it for the same
  // reason (shouldIgnoreUpsellClick).
  document.addEventListener(
    "click",
    function (event) {
      var button = event.target.closest("[data-bf-choose]");
      var card = button || event.target.closest("[data-bf-choice]");
      if (!card) return;
      if (!button && event.target.closest("a, button, input, label, select, textarea")) return;

      var root = rootOf(card);
      if (!root) return;

      event.preventDefault();
      event.stopPropagation();
      selectChoice(
        root,
        (button || card).getAttribute("data-bf-choose") || card.getAttribute("data-bf-choice")
      );
    },
    true
  );


  // "Ändern": open the product's variant modal — programmatically, the way
  // the cart's own bridge does (setAttribute open), so a second trigger on
  // the page doesn't fight the theme's aria-controls wiring.
  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-bf-open-modal]");
    if (!button) return;
    event.preventDefault();

    var modal = document.getElementById(button.getAttribute("data-bf-open-modal") || "");
    if (!modal) return;
    // a gift row opens it: the picker shows GRATIS instead of the price (_kc-upsell-modal, data-bf-gift-modal)
    if (button.closest(".kc-bf-summary__row--gift")) modal.setAttribute("data-bf-gift-modal", "");

    if (button.hasAttribute("data-bf-preselect")) {
      // The set's modal preselects once, at construction. Re-read the row's
      // current variant so a fabric re-matched since then shows as selected.
      var selector = modal.querySelector("upsell-variant-selector");
      if (selector && typeof selector.preselectCurrentVariant === "function") {
        try {
          selector.preselectCurrentVariant();
        } catch (error) {
          /* the modal still opens; the customer can still pick */
        }
      }
    }
    modal.setAttribute("open", "");
  });

  // A pick saved in a variant modal: the cover's, or the gift set's.
  document.addEventListener("upsell:variant-changed", function (event) {
    var detail = event.detail || {};
    var key = detail.referenceCheckboxId;
    if (!key || !detail.variantId) return;

    var variantText = detail.variantOptions || detail.variantTitle || "";

    configNodes().forEach(function (config) {
      var root = rootOf(config);

      if (key === config.getAttribute("data-bf-cover-key")) {
        // a solo sofa's gift set picked by hand: the sofa's colour no longer overrides it
        if (isSolo(config)) config.setAttribute("data-bf-cover-picked", "true");
        config.setAttribute("data-bf-cover-variant-id", detail.variantId);
        if (detail.price != null) config.setAttribute("data-bf-cover-price", detail.price);
        config.setAttribute("data-bf-cover-compare", detail.compareAtPrice || 0);
        var coverRow = rowOf(root, "cover");
        setRowText(coverRow, "[data-bf-row-variant]", variantText);
        setRowImage(coverRow, detail.imageSrc, variantText);
        updateTotals(config);
      } else if (key === config.getAttribute("data-bf-set-key")) {
        applySetVariant(
          config,
          {
            id: detail.variantId,
            price: detail.price != null ? detail.price : null,
            compareAt: detail.compareAtPrice || 0,
            title: variantText,
            img: detail.imageSrc,
          },
          true
        );
        updateTotals(config);
      }
    });
  });

  // The sofa's variant changed. This block is preserved across the section
  // re-render that follows, so its own row, the fabric-matched gift and the
  // totals are brought up to date here, before the clone is taken.
  document.addEventListener("variant:change", function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    configNodes().forEach(function (config) {
      applySofaVariant(config, variant);

      if (isSolo(config) && !config.hasAttribute("data-bf-cover-picked")) {
        var gift = matchSetVariant(config, variant.option1);
        if (gift && String(gift.id) !== config.getAttribute("data-bf-cover-variant-id")) applySoloGift(config, gift);
      }

      if (!config.hasAttribute("data-bf-set-picked")) {
        var match = matchSetVariant(config, variant.option1);
        if (match && String(match.id) !== config.getAttribute("data-bf-set-variant-id")) {
          applySetVariant(config, { id: match.id, price: match.p, compareAt: match.cp || 0, title: match.t, img: match.img }, false);
        }
      }

      updateTotals(config);
    });
  });

  // The section came back from the Section Rendering API: the price list and
  // the badge are freshly-rendered Liquid again, showing the sofa alone. The
  // preserved upsell block still holds the true state, so it is re-applied.
  document.addEventListener("variant:rerendered", function () {
    setTimeout(function () {
      configNodes().forEach(function (config) {
        renderChoice(rootOf(config));
        updateTotals(config);
      });
    }, 0);
  });

  // The add-to-cart: the two gifts ride in the same request as sofa + cover.
  document.addEventListener("upsell:collect-items", function (event) {
    var detail = event.detail || {};
    var items = detail.items;
    if (!items || typeof items.push !== "function") return;

    // One pair per submit, however many upsell blocks the page carries.
    var alreadyIn = items.some(function (item) {
      return item && item.properties && item.properties[GIFT_PROPERTY];
    });
    if (alreadyIn) return;

    var configs = configNodes();
    for (var i = 0; i < configs.length; i++) {
      var config = configs[i];
      var root = rootOf(config);
      if (!coverChecked(root)) continue;

      // Only when the cover really is in THIS submit: upsell.js applies its
      // own visibility and AND-across-blocks rules to build `items`, and the
      // gifts must not outrun them. The slot input is what upsell.js read.
      var coverKey = config.getAttribute("data-bf-cover-key");
      var slot = root.querySelector('input[data-upsell-item][data-input-id="' + coverKey + '"]');
      var coverVariantId = num(slot ? slot.value : config.getAttribute("data-bf-cover-variant-id"));
      var coverInItems = items.some(function (item) {
        return item && num(item.id) === coverVariantId;
      });
      if (!coverVariantId || !coverInItems) continue;

      // Solo: the card's item already IS the gift set; it is tagged as the gift, and only the Schutzset is added.
      if (isSolo(config)) {
        items.forEach(function (item) {
          if (item && num(item.id) === coverVariantId) {
            item.properties = item.properties || {};
            item.properties[GIFT_PROPERTY] = config.getAttribute("data-bf-solo-gift") || "kissen-set-8";
          }
        });
        var soloSchutzId = num(config.getAttribute("data-bf-schutzset-variant-id"));
        if (soloSchutzId) {
          var soloSchutzProps = {};
          soloSchutzProps[GIFT_PROPERTY] = config.getAttribute("data-bf-schutzset") || "komplettes-schutzset";
          items.push({ id: soloSchutzId, quantity: 1, properties: soloSchutzProps });
        }
        return;
      }

      var setVariantId = num(config.getAttribute("data-bf-set-variant-id"));
      var schutzsetVariantId = num(config.getAttribute("data-bf-schutzset-variant-id"));
      var giftA = config.getAttribute("data-bf-gift-a") || "kissen-set-1";
      var schutzset = config.getAttribute("data-bf-schutzset") || "komplettes-schutzset";

      if (setVariantId) {
        var setProperties = {};
        setProperties[GIFT_PROPERTY] = giftA;
        items.push({ id: setVariantId, quantity: 1, properties: setProperties });
      }
      if (schutzsetVariantId) {
        var schutzsetProperties = {};
        schutzsetProperties[GIFT_PROPERTY] = schutzset;
        items.push({ id: schutzsetVariantId, quantity: 1, properties: schutzsetProperties });
      }
      return;
    }
  });

  // ── The reminder ──────────────────────────────────────────────────────────
  // Add-to-cart with the cover unticked opens a small dialog instead. Both
  // answers resume the intercepted click, so the sale always proceeds — the
  // dialog only decides whether the cover (and with it the two gifts) rides
  // along.

  /** True only while re-issuing the click the dialog swallowed, so that one
   *  synthetic click passes straight through. Deliberately NOT an "already
   *  answered" flag: the reminder is shown on EVERY add-to-cart while the
   *  cover is untaken, since each add is a fresh chance to take it. */
  var resumingClick = false;
  /** The add-to-cart button whose click the dialog swallowed. */
  var pendingTrigger = null;
  /**
   * Which picker the swallowed click is waiting on: "cover", then "set",
   * then nothing. The dialog hands the customer both fabrics in a row rather
   * than adding either blind.
   */
  var awaitingVariant = null;
  /** Between the cover's save and the set's picker opening — the cover's
   *  drawer closes in that gap and must not be read as "backed out". */
  var chainingToSet = false;
  /** True while the switch demo is playing, so a second add-to-cart during
   *  it goes straight through instead of re-opening the dialog. */
  var demoRunning = false;
  /** One beat, matched to the kc-bf-demo-* keyframes in the block's CSS. */
  var DEMO_MS = 1500;

  /**
   * The dialog, moved to <body>.
   *
   * It is authored inside the upsell block, which is buried in the product
   * form — under ancestors that clip (`overflow: hidden`) and that create
   * stacking contexts. Inside one of those, `position: fixed` stops being
   * relative to the viewport and z-index cannot climb past the ancestor's own
   * layer, so the dialog came out cropped and behind the page. As a direct
   * child of <body> there is nothing above it to escape from.
   *
   * Deduped because `product:rerender` can inject a second copy of the
   * block's markup: the newest one wins, older portaled copies are dropped.
   */
  function popupElement() {
    var found = document.querySelectorAll("[data-bf-popup]");
    if (!found.length) return null;

    var popup = found[found.length - 1];
    for (var i = 0; i < found.length - 1; i++) found[i].remove();

    if (popup.parentNode !== document.body) document.body.appendChild(popup);
    return popup;
  }

  /**
   * The demo the reminder leaves behind. Turning the offer down closes the
   * dialog on a page where the set card is still one tap away, and a
   * customer who has just dismissed a dialog is not reading the card behind
   * it. So the card plays the switch once — it lifts, and its button fills
   * the way it would under a finger — and only then does the purchase carry
   * on. Shown, not explained, and in the place where the action lives.
   *
   * It never blocks the sale: `done` runs after the beat, immediately when
   * there is no card to play or the customer asked for less motion.
   */
  function runSwitchDemo(done) {
    var config = untakenConfig();
    var root = config ? rootOf(config) : null;
    var card = root ? root.querySelector('[data-bf-choice="set"]') : null;
    var reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!card || reduced) {
      done();
      return;
    }

    // Played where it is. Scrolling to it would move the page under a
    // customer who is mid-purchase, which is exactly what this block no
    // longer does anywhere.
    demoRunning = true;
    card.classList.add("is-demo");
    setTimeout(function () {
      card.classList.remove("is-demo");
      demoRunning = false;
      done();
    }, DEMO_MS);
  }

  function closePopupAndResume(withDemo) {
    var popup = popupElement();
    if (popup) popup.hidden = true;

    var trigger = pendingTrigger;
    pendingTrigger = null;
    if (!trigger) return;

    function fire() {
      // click() dispatches synchronously, so the flag is down again before
      // any later click can reach the gate.
      resumingClick = true;
      try {
        trigger.click();
      } finally {
        resumingClick = false;
      }
    }

    if (withDemo) {
      runSwitchDemo(fire);
      return;
    }
    fire();
  }

  /**
   * Opens the Kissen-Set's own picker, the one blocks/_kc-upsell-modal.liquid
   * renders for the gift. Returns false when the page carries none, so the
   * caller can fall through instead of waiting for a dialog that will never
   * open.
   */
  function openSetPicker(config) {
    var id = config && config.getAttribute("data-bf-set-product-id");
    var modal = id ? document.getElementById("upsell-modal-" + id) : null;
    if (!modal) return false;

    // The picker preselects once, at construction; re-read the row's current
    // variant so a fabric re-matched since then shows as selected.
    var selector = modal.querySelector("upsell-variant-selector");
    if (selector && typeof selector.preselectCurrentVariant === "function") {
      try {
        selector.preselectCurrentVariant();
      } catch (error) {
        /* the modal still opens; the customer can still pick */
      }
    }
    modal.setAttribute("open", "");
    return true;
  }

  /**
   * Finishes the interrupted add-to-cart once the customer is done with the
   * fabric picker — whether they chose or backed out.
   *
   * Either way the click they made before the dialog appeared has to
   * complete. Leaving it pending would mean they pressed "add to cart" and
   * the page simply never answered.
   */
  function resumeAfterVariant() {
    if (!awaitingVariant) return;
    awaitingVariant = null;
    chainingToSet = false;
    closePopupAndResume();
  }

  /** The first offer block whose cover the customer has not taken. */
  function untakenConfig() {
    var configs = configNodes();
    for (var i = 0; i < configs.length; i++) {
      if (!coverChecked(rootOf(configs[i]))) return configs[i];
    }
    return null;
  }

  function handlePopupGate(event) {
    // The dialog's own controls first.
    if (event.target.closest("[data-bf-popup-add]")) {
      var config = untakenConfig();
      var root = config ? rootOf(config) : null;
      var checkbox = root ? coverCheckbox(root) : null;

      // Ticked first, so the answer is recorded whatever happens next: the
      // customer has said yes, and backing out of the picker should not undo
      // that — it should leave the fabric the card already matched to the
      // sofa. Bubbles, so upsell.js enables the cover's slot inputs and this
      // file opens the summary, exactly as a real tick would.
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Then straight into the fabric picker: a cover has a fabric, and
      // taking it blind gets it wrong most of the time. Choosing there IS the
      // confirmation — the swallowed add-to-cart resumes as soon as the
      // drawer is done, with no second trip through this dialog.
      var picker = root
        ? root.querySelector(".upsell-block-item--bf-cover .upsell-variant-selector button")
        : null;

      if (picker) {
        var popupToHide = popupElement();
        if (popupToHide) popupToHide.hidden = true;
        awaitingVariant = "cover";
        picker.click();
        return;
      }

      // No picker rendered: carry on rather than stranding the customer with
      // a button that does nothing.
      closePopupAndResume();
      return;
    }

    // The backdrop and the explicit "without it" button behave the same:
    // continue the purchase, just without the cover. Closing into a dead end
    // would silently cancel the add-to-cart the customer asked for.
    if (event.target.closest("[data-bf-popup-skip]")) {
      closePopupAndResume(true);
      return;
    }

    if (event.target.closest("[data-bf-popup]")) return;

    // The gate itself: an add-to-cart click while the cover is untaken.
    if (resumingClick || demoRunning) return;
    if (!untakenConfig()) return;

    var button = event.target.closest("button");
    if (!button || button.type !== "submit") return;

    var form = button.form;
    if (!form || !form.querySelector('input[name="id"]')) return;

    var popup = popupElement();
    if (!popup) return;

    // Capture phase + stopImmediatePropagation: this runs before the form
    // submits AND before upsell.js's own document-level intercept, so nothing
    // downstream fires until the click is re-issued.
    event.preventDefault();
    event.stopImmediatePropagation();

    pendingTrigger = button;
    popup.hidden = false;

    var addButton = popup.querySelector("[data-bf-popup-add]");
    if (addButton) addButton.focus();
  }

  document.addEventListener("click", handlePopupGate, true);

  // The picker's save broadcasts the pick and the drawer then closes on its
  // own. Saving the COVER hands the customer the gift's fabric next; saving
  // that one finishes the interrupted add-to-cart. Everything waits out the
  // drawer's close animation, so one dialog is never opened over another.
  document.addEventListener("upsell:variant-changed", function (event) {
    if (!awaitingVariant) return;

    var key = (event.detail || {}).referenceCheckboxId;
    var config = configNodes()[0];
    if (!config) return;

    if (awaitingVariant === "cover" && key === config.getAttribute("data-bf-cover-key")) {
      awaitingVariant = "set";
      chainingToSet = true;
      setTimeout(function () {
        chainingToSet = false;
        // No picker for the gift on this page: the fabric-matched one stands.
        if (!openSetPicker(config)) resumeAfterVariant();
      }, 400);
      return;
    }

    if (awaitingVariant === "set" && key === config.getAttribute("data-bf-set-key")) {
      setTimeout(resumeAfterVariant, 350);
    }
  });

  // Backing out of either picker resumes the purchase — with the cover, since
  // the tick above already recorded the customer's answer, and with whatever
  // fabric the gift currently carries.
  document.addEventListener("dialog:after-hide", function (event) {
    if (!awaitingVariant || chainingToSet) return;
    if (!event.target.closest || !event.target.closest(".kc-modal")) return;
    setTimeout(resumeAfterVariant, 50);
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    var popup = document.querySelector("[data-bf-popup]");
    // Same contract as the backdrop: proceed, without the cover.
    if (popup && !popup.hidden) closePopupAndResume(true);
  });

  // First paint: the cards and the panel follow the checkbox's initial state.
  // The cover ships ticked while the campaign is live (the item block does
  // that in Liquid), so the page opens on the set with the choice visible
  // rather than on a tick the customer has to find.
  function init() {
    popupElement();
    configNodes().forEach(function (config) {
      renderChoice(rootOf(config));
      updateTotals(config);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
